<template>
  <aside class="workplace-detail" :aria-label="$t('building.plan.details')">
    <template v-if="workplace">
      <div class="detail-heading">
        <div><h3>{{ workplace.place_code }}</h3><span class="detail-location">{{ workplace.locationName }}</span></div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }"><v-btn icon small v-bind="attrs" v-on="on" :aria-label="$t('building.plan.close')" @click="$emit('close')"><v-icon small>mdi-close</v-icon></v-btn></template>
          {{ $t('building.plan.close') }}
        </v-tooltip>
      </div>
      <div class="detail-actions">
        <v-chip small label :color="statusColor" :text-color="workplace.state === 'expired' ? 'white' : undefined">{{ $t('building.plan.states.' + workplace.state) }}</v-chip>
        <span v-if="workplace.active.length > 1" class="detail-shared"><v-icon small>mdi-account-multiple</v-icon> {{ workplace.active.length }}</span>
        <v-spacer />
        <v-tooltip bottom v-for="action in actions" :key="action.key">
          <template v-slot:activator="{ on, attrs }"><v-btn icon small v-bind="attrs" v-on="on" :aria-label="action.label" @click="$emit(action.key)"><v-icon small>{{ action.icon }}</v-icon></v-btn></template>
          {{ action.label }}
        </v-tooltip>
      </div>
      <v-alert v-if="!workplace.registered" dense text type="info">{{ $t('building.plan.unregistered') }}</v-alert>
      <v-alert v-else-if="!workplace.geometry" dense text type="info">{{ $t('building.plan.noGeometry') }}</v-alert>
      <dl class="detail-fields">
        <div v-for="field in fields" :key="field.key" class="detail-field">
          <dt>{{ field.label }}</dt>
          <dd>
            <router-link v-if="field.to && field.value" :to="field.to">{{ field.value }}</router-link><span v-else>{{ field.value || '-' }}</span>
            <v-tooltip v-if="field.value" bottom>
              <template v-slot:activator="{ on, attrs }"><v-btn icon x-small v-bind="attrs" v-on="on" :aria-label="$t('building.plan.copyField', { field: field.label })" @click="$emit('copy', String(field.value))"><v-icon small>mdi-content-copy</v-icon></v-btn></template>
              {{ $t('building.plan.copyField', { field: field.label }) }}
            </v-tooltip>
          </dd>
        </div>
      </dl>
      <section class="detail-assignments">
        <h4>{{ $t('building.plan.assignedPeople') }} <span>{{ workplace.active.length }}</span></h4>
        <p v-if="!workplace.active.length" class="detail-empty">{{ $t('building.plan.noAssignments') }}</p>
        <PlanAssignment v-for="assignment in workplace.active" :key="workplace.key + '-' + assignment.id" :assignment="assignment" :admin="admin" :endpoint="endpoint" interactive @photo="openPhoto" />
      </section>
      <details v-if="admin && workplace.history.length" class="detail-history">
        <summary>{{ $t('building.plan.history') }} ({{ workplace.history.length }})</summary>
        <div v-for="assignment in workplace.history" :key="assignment.id" class="history-person">
          <router-link v-if="assignment.personId" :to="'/manage/edit/researchers/edit/' + assignment.personId">{{ assignment.name }}</router-link><span v-else>{{ assignment.name }}</span>
          <time v-if="assignment.expiration" :datetime="assignment.expiration">{{ formatDate(assignment.expiration) }}</time>
        </div>
      </details>
    </template>
    <div v-else class="detail-placeholder"><v-icon size="36">mdi-desk</v-icon><span>{{ $t('building.plan.noSelection') }}</span></div>
    <v-dialog v-if="selectedPhoto" v-model="photoOpen" max-width="520" aria-labelledby="plan-photo-title">
      <v-card class="photo-dialog">
        <div class="photo-dialog-heading">
          <p id="plan-photo-title">{{ selectedPhoto.name }}</p>
          <v-btn icon small :aria-label="$t('building.plan.closePhoto')" @click="photoOpen = false"><v-icon small>mdi-close</v-icon></v-btn>
        </div>
        <div class="photo-dialog-image" :aria-busy="String(photoLoading)">
          <img v-if="photoOpen && !photoFailed" :src="photoSource" :alt="selectedPhoto.name" @load="photoLoading = false" @error="photoError" />
          <v-progress-circular v-if="photoLoading" class="photo-dialog-loading" indeterminate size="28" width="2" :aria-label="$t('building.plan.loading')" />
          <p v-if="photoFailed" role="alert">{{ $t('building.plan.photoUnavailable') }}</p>
        </div>
        <div v-if="selectedPhoto.temporaryPhoto" class="photo-dialog-caption"><v-icon small>mdi-clock-outline</v-icon>{{ $t('building.plan.temporaryPhoto') }}</div>
      </v-card>
    </v-dialog>
  </aside>
</template>

<script>
import { photoUrl } from './planModel';
import PlanAssignment from './PlanAssignment';
export default {
  components: { PlanAssignment },
  props: { workplace: Object, admin: Boolean, endpoint: { type: String, required: true } },
  data: () => ({ photoOpen: false, selectedPhoto: null, photoSource: '', photoLoading: false, photoFailed: false }),
  watch: {
    workplace(next, previous) { if (!next || !previous || next.key !== previous.key) this.photoOpen = false; },
    admin(value) { if (!value) { this.photoOpen = false; this.selectedPhoto = null; } }
  },
  computed: {
    statusColor() { return { expired: 'error', expiring: 'amber lighten-3', current: 'green lighten-5', occupied: 'grey lighten-3', free: 'grey lighten-4' }[this.workplace.state]; },
    actions() {
      const actions = [{ key: 'share', icon: 'mdi-link-variant', label: this.$t('building.plan.copyLink') }];
      if (this.workplace.geometry) actions.unshift({ key: 'focus', icon: 'mdi-crosshairs-gps', label: this.$t('building.plan.centerDesk') });
      return actions;
    },
    fields() {
      const desk = this.workplace, host = desk.host || {};
      const t = key => this.$t('building.plan.' + key);
      const fields = [
        { key: 'code', label: t('desk'), value: desk.place_code, to: this.admin && desk.registered ? { path: '/manage/edit/workplaces/', hash: '#' + desk.place_code } : null },
        { key: 'name', label: t('name'), value: desk.name },
        { key: 'cabinet', label: t('cabinet'), value: desk.file_cabinet }, { key: 'socket', label: t('socket'), value: desk.network_socket },
        { key: 'phone', label: t('phone'), value: desk.extension },
        { key: 'host', label: t('computer'), value: host.hostname, to: this.admin && host.ip ? { path: '/manage/edit/hosts/', hash: '#' + host.ip } : null },
        { key: 'ip', label: 'IP', value: host.ip, to: this.admin && host.ip ? { path: '/manage/edit/hosts/', hash: '#' + host.ip } : null }
      ];
      if (this.admin || host.mac) fields.push({ key: 'mac', label: 'MAC', value: host.mac });
      return fields;
    }
  },
  methods: {
    photoUrl,
    openPhoto(assignment) {
      this.selectedPhoto = assignment; this.photoSource = photoUrl(this.endpoint, assignment.photo, true);
      this.photoLoading = true; this.photoFailed = false; this.photoOpen = true;
    },
    photoError() {
      const thumbnail = photoUrl(this.endpoint, this.selectedPhoto.photo);
      if (this.photoSource !== thumbnail) { this.photoSource = thumbnail; return; }
      this.photoLoading = false; this.photoFailed = true;
    },
    formatDate(value) { const date = new Date(value + 'T12:00:00'); const locale = this.$i18n.locale === 'gl' ? 'es-ES' : this.$i18n.locale; return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(locale).format(date); }
  }
};
</script>

<style scoped>
.workplace-detail { min-width: 0; padding: 20px; overflow-y: auto; border-left: 1px solid #dfe3e4; background: #fff; scroll-margin-top: 76px; }
.detail-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.detail-heading h3 { font-size: 22px; font-weight: 600; margin: 0; overflow-wrap: anywhere; }
.detail-location { color: #637076; font-size: 13px; }
.detail-actions { display: flex; align-items: center; gap: 6px; margin: 12px 0 16px; }
.detail-shared { font-size: 13px; display: inline-flex; align-items: center; gap: 3px; }
.detail-fields { margin: 0; }
.detail-field { display: grid; grid-template-columns: 90px minmax(0, 1fr); align-items: start; gap: 10px; padding: 5px 0; font-size: 13px; }
.detail-field dt { color: #637076; line-height: 24px; }
.detail-field dd { margin: 0; display: flex; align-items: center; justify-content: space-between; gap: 5px; min-width: 0; }
.detail-field dd span, .detail-field dd a { overflow-wrap: anywhere; line-height: 24px; }
.detail-field dd .v-btn { flex: 0 0 auto; }
.detail-assignments { margin-top: 22px; }
.detail-assignments h4 { font-size: 14px; margin: 0; padding-bottom: 8px; border-bottom: 1px solid #e8ebec; }
.detail-assignments h4 span { font-weight: normal; color: #637076; margin-left: 4px; }
.detail-empty { font-size: 13px; color: #637076; margin-top: 12px; }
.detail-history { margin-top: 18px; font-size: 13px; }
.detail-history summary { cursor: pointer; font-weight: 600; padding: 6px 0; }
.history-person { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid #e8ebec; }
.history-person a, .history-person span { overflow-wrap: anywhere; }
.history-person time { flex-shrink: 0; font-size: 12px; color: #637076; }
.detail-placeholder { display: flex; min-height: 220px; align-items: center; justify-content: center; flex-direction: column; gap: 12px; color: #637076; font-size: 14px; }
.photo-dialog { overflow: hidden; border-radius: 6px; }
.photo-dialog-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 16px; }
.photo-dialog-heading p { margin: 0; font-size: 15px; font-weight: 600; color: #343b3d; overflow-wrap: anywhere; }
.photo-dialog-heading .v-btn { flex-shrink: 0; }
.photo-dialog-image { position: relative; display: flex; align-items: center; justify-content: center; aspect-ratio: 1; max-height: min(65vh, 560px); max-height: min(65dvh, 560px); background: #f0f2f2; }
.photo-dialog-image img { position: absolute; inset: 0; display: block; width: 100%; height: 100%; object-fit: contain; }
.photo-dialog-image p { padding: 24px; margin: 0; font-size: 13px; color: #637076; text-align: center; }
.photo-dialog-loading { position: absolute; color: #637076; }
.photo-dialog-caption { display: flex; align-items: center; gap: 6px; padding: 10px 16px; font-size: 12px; color: #637076; }
@media (max-width: 900px) {
  .workplace-detail { border-left: 0; border-top: 1px solid #dfe3e4; padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px)); }
}
</style>
